import React, { useState, useEffect, useCallback, useMemo } from "react";

// Episode components
import { TranscriptSection } from "@/components/episode/TranscriptSection";
import { QuizSection, type QuizItem } from "@/components/episode/QuizSection";
import { SummarySection } from "@/components/episode/SummarySection";
import { MindMapSection } from "@/components/episode/MindMapSection";
import { VocabularySection } from "@/components/episode/VocabularySection";
import { PhraseSection } from "@/components/episode/PhraseSection";
import {
  ImmersiveVocabularyCards,
  ActiveVocabularyTermData,
  normalizeCefrLevel,
} from "@/components/episode/ImmersiveVocabularyCards";
import { getTermColorInfo } from "@/components/vocabulary/term-card-backgrounds";
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key";
import { resolveDictionaryDisplayName } from "@/lib/vocabulary/dictionary-display";
import { demoLearningSidebarData, type LearningSidebarData } from "@/lib/demo-learning-data";
import { SpinnerIcon } from "../ui/SpinnerIcon";

// Access control components
import { PremiumPrompt } from "@/components/ui/PremiumPrompt";

// Hooks
import { useUser } from "@/lib/context/user-context";
import { useUsage } from "@/contexts/usage-context";
import { useI18n } from "@/lib/i18n";

// interface LoginPromptProps {
//   onLogin: () => void;
// }

// const LoginPrompt: React.FC<LoginPromptProps> = ({ onLogin }) => (
//   <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
//     <div className="bg-primary/50 rounded-full p-4 mb-4">
//       <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
//         <path d="M7 14a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v2h-4v-2a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v6H5v-6z"/>
//       </svg>
//     </div>
//     <h3 className="text-lg font-medium mb-2">Sign in required</h3>
//     <p className="text-sm text-gray-500 mb-4 max-w-sm">
//       Please sign in to access this feature and unlock all capabilities
//     </p>
//     <button
//       onClick={onLogin}
//       className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors"
//     >
//       Sign In
//     </button>
//   </div>
// );

interface ContentRendererProps {
  activeTab: string;
  user: any;
  session?: any;
  onLogin: () => void;
  
  // Summary props
  summary: string;
  isSummaryLoading: boolean;
  onGenerateSummary: () => void | Promise<void>;
  onSummaryTranslationStateChange?: (showTranslation: boolean) => void;
  
  // Mindmap props
  mindmapData: string;
  content: any;
  isLoading: boolean;
  onGenerateMindmap: () => void | Promise<void>;
  onTimestampClick: (timestamp: number, endTimestamp?: number) => void;
  
  // Transcript props
  groupedSentences: any[];
  youtubeTime: number;
  ytPlayer: any;
  userSettings: any;
  wordHighlightEnabled: boolean;
  practiceMode: boolean;
  practiceModeType: "vocabulary" | "toefl" | "shadowing";
  vocabularyTerms: string[];
  setIsPlaying: (playing: boolean) => void;
  showTranslation: boolean;
  isWordCheckerReady: boolean;
  isVideoMinimized?: boolean;
  episodeDuration?: number; // 🔥 新增：视频时长（秒）
  onTranscriptUpdate?: (transcript: any[], audioUrl?: string) => void;
  
  // Vocabulary props
  vocabulary: any;
  object: any;
  isVocabularyLoading: boolean;
  onGenerateVocabulary: () => void | Promise<void>;
  onVocabularyUpdate?: (vocabulary: any) => void;
  onVocabularyCardSwipe?: (
    payload: { term: string; scenarioName?: string },
    direction: "left" | "right"
  ) => void;
  hiddenVocabularyTermKeys?: string[];
  onFilterHiddenTermsChange?: (keys: string[]) => void;
  
  // Clean Read props
  cleanReadData: any;
  isCleanReadLoading: boolean;
  onGenerateCleanRead: () => void | Promise<void>;
  
  // Focus Mode props
  isFocusMode?: boolean;
  onFocusModeChange?: (focusMode: boolean) => void;
  
  // Learning Sidebar props
  learningSidebarData?: LearningSidebarData;
  onGenerateLearningSidebar?: () => void | Promise<void>;
  
  // Data persistence props
  episodeId?: string;
  episodeData?: any; // 🔥 新增：传递完整的episode数据用于恢复
  saveToRedis?: (endpoint: string, data: any, field: string) => Promise<void>;
  
  // Access info (from useEpisodeData)
  accessInfo?: {
    hasAccess: boolean;
    isFeaturedEpisode: boolean;
    accessLevel: 'guest' | 'free' | 'premium';
    reason: string;
  };
  
  // Generation records
  generationRecords?: {
    transcript: boolean;
    summary: boolean;
    vocabulary: boolean;
    mindmap: boolean;
    cleanread: boolean;
  };
  hasTwoBrokeGirlsAccess?: boolean;
  // 🔥 是否允许 Basic Plan Lite 用户访问
  allowBasicPlanLite?: boolean;
  
  // Immersive mode props
  isImmersiveMode?: boolean;
  activeSubtitleId?: string | null;
  onActiveSubtitleChange?: (
    subtitleId: string | null,
    subtitleText?: string,
    subtitleTranslation?: string,
    subtitleTimestamp?: [number, number]
  ) => void;
  onActiveVocabularyTermsChange?: (terms: ActiveVocabularyTermData[]) => void;
  showTranslationInImmersive?: boolean;
  revealAllTranslationsInImmersive?: boolean;
  
// Transcript update callback
  // onTranscriptUpdate?: (transcript: any[]) => void;
}

export const ContentRenderer: React.FC<ContentRendererProps> = ({
  activeTab,
  // user,
  session,
  onLogin,
  summary,
  isSummaryLoading,
  onGenerateSummary,
  onSummaryTranslationStateChange,
  mindmapData,
  content,
  isLoading,
  onGenerateMindmap,
  onTimestampClick,
  groupedSentences,
  youtubeTime,
  ytPlayer,
  userSettings,
  wordHighlightEnabled,
  practiceMode,
  practiceModeType,
  vocabularyTerms,
  setIsPlaying,
  showTranslation,
  isWordCheckerReady,
  isVideoMinimized = false,
  episodeDuration,
  vocabulary,
  object,
  isVocabularyLoading,
  onGenerateVocabulary,
  onVocabularyUpdate,
  onVocabularyCardSwipe,
  hiddenVocabularyTermKeys,
  onFilterHiddenTermsChange,
  cleanReadData,
  isCleanReadLoading,
  isFocusMode = false,
  onFocusModeChange,
  learningSidebarData,
  onGenerateLearningSidebar,
  episodeId,
  episodeData, // 🔥 新增：接收episode数据
  saveToRedis,
  accessInfo,
  generationRecords, // 🔥 新增：接收生成记录
  allowBasicPlanLite = false, // 🔥 是否允许 Basic Plan Lite 用户访问
  isImmersiveMode = false,
  activeSubtitleId,
  onActiveSubtitleChange,
  onActiveVocabularyTermsChange,
  showTranslationInImmersive = true,
  revealAllTranslationsInImmersive = false,
  onTranscriptUpdate,
  hasTwoBrokeGirlsAccess: hasTwoBrokeGirlsAccessProp = false,
}) => {
  // Hooks for user and usage information
  const { user } = useUser();
  const { usageInfo } = useUsage();
  const { t } = useI18n();
  const defaultAccessReason = t("premiumPrompt.fallback");
  const dictionaryDisplayName = useMemo(() => {
    const dictionaryFile = userSettings?.dictionarySetting;
    if (!dictionaryFile) {
      return undefined;
    }
    return resolveDictionaryDisplayName(dictionaryFile);
  }, [userSettings?.dictionarySetting]);

  const quizItems = useMemo(() => {
    const vocabularyQuiz = (vocabulary as any)?.quiz;
    if (Array.isArray(vocabularyQuiz)) {
      return vocabularyQuiz;
    }

    const deepQuiz = content?.content?.content?.vocabulary?.quiz;
    if (Array.isArray(deepQuiz)) {
      return deepQuiz;
    }

    const midQuiz = content?.content?.vocabulary?.quiz;
    if (Array.isArray(midQuiz)) {
      return midQuiz;
    }

    const shallowQuiz = (content as any)?.vocabulary?.quiz;
    if (Array.isArray(shallowQuiz)) {
      return shallowQuiz;
    }

    return [];
  }, [content, vocabulary]);

  const staticVocabularyHighlights = useMemo<ActiveVocabularyTermData[]>(() => {
    const scenarios = vocabulary?.scenarios;
    if (!Array.isArray(scenarios) || scenarios.length === 0) {
      return [];
    }

    const hiddenKeys = new Set(hiddenVocabularyTermKeys ?? []);
    const unique = new Map<string, ActiveVocabularyTermData>();

    scenarios.forEach((scenario: any) => {
      if (!scenario?.terms) return;

      scenario.terms.forEach((term: any) => {
        const termName = term?.term;
        if (!termName) {
          return;
        }

        const key = createVocabularyTermKey(termName, scenario.name);
        if (!key || hiddenKeys.has(key) || unique.has(key)) {
          return;
        }

        const colorInfo = getTermColorInfo(termName, scenario.name);
        
        // 🎯 提取第一个 original 类型的例句（与 VocabularyDisplay 一致）
        const examples = Array.isArray(term.examples) ? term.examples : [];
        const primaryExample = examples.find((ex: any) => ex?.type === 'original') || examples[0];
        
        unique.set(key, {
          term: termName,
          scenarioName: scenario.name,
          colorIndex: colorInfo.colorIndex,
          backgroundClass: colorInfo.backgroundClass,
          lightBackgroundClass: colorInfo.lightBackgroundClass,
          darkBackgroundClass: colorInfo.darkBackgroundClass,
          textClass: colorInfo.textClass,
          level: normalizeCefrLevel(term.level),
          // 🎯 包含 vocabulary 数据用于 hover card 显示
          explanation: term.explanation,
          context_description: term.context_description,
          example: primaryExample ? {
            sentence: primaryExample.sentence || primaryExample.english,
            translation: primaryExample.translation,
          } : undefined,
        });
      });
    });

    return Array.from(unique.values());
  }, [vocabulary, hiddenVocabularyTermKeys]);

  const hasDictionarySelected = useMemo(
    () => Boolean(userSettings?.dictionarySetting),
    [userSettings?.dictionarySetting]
  );

  const resolvedEpisodeTitle =
    content?.content?.title || episodeData?.content?.title;
  const resolvedEpisodeThumbnail =
    content?.content?.thumbnail || episodeData?.content?.thumbnail;
  const resolvedEpisodeId =
    episodeId || content?.content?.shareId || content?.shareId || episodeData?.shareId;

  const isTwoBrokeGirlsEpisode = useMemo(() => {
    if (!resolvedEpisodeId) return false;
    return resolvedEpisodeId.toString().toUpperCase().startsWith('PCJMS0');
  }, [resolvedEpisodeId]);

  const metadataTwoBrokeGirlsAccess = useMemo(() => {
    const flag = user?.user_metadata?.twoBrokeGirlsAccess === true;
    if (!flag) return false;

    const expiresAt = user?.user_metadata?.twoBrokeGirlsAccessExpiresAt;
    if (!expiresAt) {
      return true;
    }

    const expiresDate = new Date(expiresAt);
    if (Number.isNaN(expiresDate.getTime())) {
      return true;
    }

    return expiresDate > new Date();
  }, [user?.user_metadata?.twoBrokeGirlsAccess, user?.user_metadata?.twoBrokeGirlsAccessExpiresAt]);

  const hasTwoBrokeGirlsAccess = useMemo(() => {
    if (!isTwoBrokeGirlsEpisode) {
      return false;
    }

    return hasTwoBrokeGirlsAccessProp || metadataTwoBrokeGirlsAccess;
  }, [isTwoBrokeGirlsEpisode, metadataTwoBrokeGirlsAccess, hasTwoBrokeGirlsAccessProp]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[TwoBrokeGirls] ContentRenderer access debug', {
        episodeId: resolvedEpisodeId,
        isTwoBrokeGirlsEpisode,
        hasTwoBrokeGirlsAccessProp,
        metadataTwoBrokeGirlsAccess,
        resolvedAccess: hasTwoBrokeGirlsAccess,
        userMetadata: user?.user_metadata,
      });
    }
  }, [
    resolvedEpisodeId,
    isTwoBrokeGirlsEpisode,
    hasTwoBrokeGirlsAccessProp,
    metadataTwoBrokeGirlsAccess,
    hasTwoBrokeGirlsAccess,
    user?.user_metadata,
  ]);

  const handleQuizItemsUpdate = useCallback(
    async (updatedQuiz: QuizItem[]) => {
      if (!onVocabularyUpdate) return;

      const baseVocabulary =
        (vocabulary && typeof vocabulary === "object" ? vocabulary : undefined) ??
        (object && typeof object === "object" ? object : undefined) ??
        (content?.content?.content?.vocabulary && typeof content.content.content.vocabulary === "object"
          ? content.content.content.vocabulary
          : {});

      const newVocabulary = {
        ...baseVocabulary,
        quiz: updatedQuiz.map((item) => ({ ...item })),
      };

      onVocabularyUpdate(newVocabulary);

      if (saveToRedis && episodeId) {
        try {
          await saveToRedis("vocabulary", newVocabulary, "vocabulary");
        } catch (error) {
          console.error("Failed to persist quiz audio updates", error);
        }
      }
    },
    [onVocabularyUpdate, vocabulary, object, content, saveToRedis, episodeId]
  );

  // Episode access control - use provided accessInfo instead of separate hook
  const hasAccess = accessInfo?.hasAccess ?? false;
  const isFeaturedEpisode = accessInfo?.isFeaturedEpisode ?? false;
  const accessLoading = !accessInfo; // Loading if no access info provided

  const isPremiumUser = usageInfo?.isPremium === true || user?.user_metadata?.isPremium === true;
  const isBasicPlanLiteUser = usageInfo?.planType === 'basic_lite' || usageInfo?.isBasicPlanLite === true || user?.user_metadata?.isBasicPlanLite === true;
  const bypassGenerationChecks = isBasicPlanLiteUser || hasTwoBrokeGirlsAccess;
  const canViewProtectedContent = hasAccess || bypassGenerationChecks;
  const isFreePlanUser = !!user && !isPremiumUser && !isBasicPlanLiteUser && !metadataTwoBrokeGirlsAccess;
  const hasTranscriptGenerationRecord = generationRecords?.transcript === true;
  const unlockedByTranscriptGeneration = isFreePlanUser && hasTranscriptGenerationRecord;

  // 🔥 判断内容是否应该显示（包含Basic Plan Lite支持）
  const shouldShowContent = (contentType: keyof typeof generationRecords) => {
    // 🎯 对于拥有特殊访问权限的用户，绕过生成记录检查，直接根据内容是否存在决定展示
    if (bypassGenerationChecks) {
      switch (contentType) {
        case 'transcript':
          return groupedSentences && groupedSentences.length > 0;
        case 'summary':
          return !!summary;
        case 'vocabulary':
          return !!(vocabulary || object);
        case 'mindmap':
          return !!(mindmapData || content?.content?.content?.mindmap);
        case 'cleanread':
          return !!cleanReadData || quizItems.length > 0;
        default:
          return false;
      }
    }

    if (unlockedByTranscriptGeneration) {
      switch (contentType) {
        case 'transcript':
          return groupedSentences && groupedSentences.length > 0;
        case 'vocabulary':
          return !!(vocabulary || object);
        default:
          break;
      }
    }

    // 如果没有生成记录信息，检查内容是否已存在
    if (!generationRecords) {
      // 根据内容类型检查是否有已存在的内容
      switch (contentType) {
        case 'transcript':
          return groupedSentences && groupedSentences.length > 0;
        case 'summary':
          return !!summary;
        case 'vocabulary':
          return !!(vocabulary || object);
        case 'mindmap':
          return !!(mindmapData || content?.content?.content?.mindmap);
        case 'cleanread':
          return !!cleanReadData || quizItems.length > 0;
        default:
          return false;
      }
    }

    // 根据生成记录决定是否显示
    if (contentType === 'cleanread') {
      return generationRecords[contentType] || quizItems.length > 0;
    }

    return generationRecords[contentType];
  };

  // Learning sidebar state - use demo data if no data provided
  const [currentLearningSidebarData, setCurrentLearningSidebarData] = useState<LearningSidebarData | undefined>(
    learningSidebarData
  );
  const [isGeneratingLearningData, setIsGeneratingLearningData] = useState(false);
  
  // Handle transcript update
  const handleTranscriptUpdate = useCallback(async (transcript: any[], audioUrl?: string) => {
    // No need to save to Redis here - generate-transcript API already saves it
    // Call parent callback to update page-level state and cache
    if (onTranscriptUpdate) {
      onTranscriptUpdate(transcript, audioUrl);
    }
  }, [onTranscriptUpdate]);

  const handleTimeDrivenSeek = useCallback((seconds: number) => {
    if (!ytPlayer) return;

    try {
      if (typeof ytPlayer.seekTo === "function") {
        ytPlayer.seekTo(seconds, true);
        return;
      }

      if (typeof ytPlayer.currentTime === "function") {
        ytPlayer.currentTime(seconds);
        return;
      }

      if (typeof ytPlayer.currentTime === "number") {
        ytPlayer.currentTime = seconds;
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("[TimeDrivenTranscript] Failed to seek player", error);
      }
    }
  }, [ytPlayer]);
  
  // Update local state when prop changes (e.g., when data is loaded from Redis)
  useEffect(() => {
    setCurrentLearningSidebarData(learningSidebarData);
  }, [learningSidebarData]);
  
  // Generate learning sidebar handler  
  const generateLearningData = async () => {
    if (onGenerateLearningSidebar) {
      onGenerateLearningSidebar();
      return;
    }

    // Extract text from cleanReadData for AI analysis
    let articleText = '';
    
    // Check if it's the new VideoContentAnalysis format
    if (cleanReadData?.videoInfo) {
      // Extract text from various parts of the new format
      const textParts = [];
      
      if (cleanReadData.blurb) textParts.push(cleanReadData.blurb);
      
      if (cleanReadData.breakdown) {
        cleanReadData.breakdown.forEach((chapter: any) => {
          if (chapter.content) textParts.push(chapter.content);
          if (chapter.keyPoints) textParts.push(...chapter.keyPoints);
        });
      }
      
      articleText = textParts.join(' ');
    } else if (cleanReadData?.sections) {
      // Old format with sections
      articleText = cleanReadData.sections
        .filter((section: any) => section.type === 'paragraph')
        .map((section: any) => section.text)
        .join(' ');
    } else {
      console.error('No clean read data available for vocabulary generation');
      // Fallback to demo data
      setCurrentLearningSidebarData(demoLearningSidebarData);
      return;
    }

    setIsGeneratingLearningData(true);
    
    try {

      if (!articleText.trim()) {
        throw new Error('No text content found for analysis');
      }

      // Call the API to generate vocabulary
      const response = await fetch('/api/generate-vocabulary-gemini', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: articleText,
          title: cleanReadData?.videoInfo?.title || null,
          channel: cleanReadData?.videoInfo?.channel || cleanReadData?.videoInfo?.author || null,
          description: cleanReadData?.videoInfo?.description || null,
          category: cleanReadData?.videoInfo?.category || null,
          tags: Array.isArray(cleanReadData?.videoInfo?.tags) ? cleanReadData.videoInfo.tags : null,
          publishDate: cleanReadData?.videoInfo?.publishDate || null,
          durationSeconds: typeof cleanReadData?.videoInfo?.durationSeconds === 'number'
            ? cleanReadData.videoInfo.durationSeconds
            : null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate vocabulary');
      }

      const vocabularyData = await response.json();
      setCurrentLearningSidebarData(vocabularyData);
      
      // Save to Redis for future use
      if (saveToRedis && episodeId) {
        await saveToRedis("learning-sidebar", vocabularyData, "learningSidebar");
      }
      
    } catch (error: any) {
      console.error('Error generating vocabulary:', error);
      
      // Show user-friendly error and fallback to demo data
      const fallbackMessage = t(
        "contentRenderer.alerts.vocabFallback",
        "Failed to generate vocabulary: {error}. Using demo data instead."
      );
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(fallbackMessage.replace("{error}", errorMessage));
      setCurrentLearningSidebarData(demoLearningSidebarData);
    } finally {
      setIsGeneratingLearningData(false);
    }
  };

  // Handle initial generation (only if no data exists)
  const handleGenerateLearningSidebar = async () => {
    // If we already have learning sidebar data, don't regenerate
    if (currentLearningSidebarData) {
      console.log('Learning sidebar data already exists, skipping generation');
      return;
    }
    
    await generateLearningData();
  };

  // Handle forced regeneration (even if data exists)
  const handleRegenerateLearningSidebar = async () => {
    await generateLearningData();
  };

  // Handle upgrade action
  const handleUpgrade = () => {
    // TODO: Implement navigation to pricing/subscription page
    console.log('Navigate to upgrade page');
    // Example: router.push('/pricing');
  };

  // Show loading state while checking access
  if (accessLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center w-full">
            <SpinnerIcon
              className="mx-auto"
              size={32}
              color="hsl(var(--muted-foreground))"
            />
            <p className="text-muted-foreground mt-4">{t("common.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    // 🚀 Immersive mode performance optimization: Render Transcript and Timeline Vocabulary
    if (isImmersiveMode) {
      const hasImmersiveAccess = canViewProtectedContent || unlockedByTranscriptGeneration;
      // In immersive mode, render TranscriptSection and ImmersiveVocabularyCards
      // But still respect access control
      return hasImmersiveAccess ? (
        <div className="immersive-content-container space-y-2">
          {/* Transcript Section */}
          <TranscriptSection
            groupedSentences={groupedSentences}
            youtubeTime={youtubeTime}
            ytPlayer={ytPlayer}
            userSettings={userSettings}
            wordHighlightEnabled={wordHighlightEnabled}
            practiceMode={practiceMode}
            practiceModeType={practiceModeType}
            vocabularyTerms={vocabularyTerms}
            setIsPlaying={setIsPlaying}
            showTranslation={showTranslation}
            isWordCheckerReady={isWordCheckerReady}
            isVideoMinimized={isVideoMinimized}
            episodeId={episodeId}
            episodeData={episodeData}
            episodeDuration={episodeDuration}
            isImmersiveMode={isImmersiveMode}
            activeSubtitleId={activeSubtitleId || undefined}
            onActiveSubtitleChange={onActiveSubtitleChange}
            onTranscriptUpdate={handleTranscriptUpdate}
            generationRecords={generationRecords}
            hasTwoBrokeGirlsAccess={hasTwoBrokeGirlsAccess}
            isTwoBrokeGirlsEpisode={isTwoBrokeGirlsEpisode}
            allowBasicPlanLite={allowBasicPlanLite}
            staticVocabularyTerms={staticVocabularyHighlights}
          />
          
          {/* Timeline Vocabulary Cards */}
          {vocabulary && (
            <div className="relative z-20">
              <ImmersiveVocabularyCards
                vocabulary={vocabulary}
                currentTime={youtubeTime}
                onTimestampClick={onTimestampClick}
                onActiveVocabularyTermsChange={onActiveVocabularyTermsChange}
                showTranslation={showTranslationInImmersive}
                revealAllTranslations={revealAllTranslationsInImmersive}
              />
            </div>
          )}
        </div>
      ) : (
        <PremiumPrompt 
          isFeatured={isFeaturedEpisode}
          accessLevel={accessInfo?.accessLevel || 'guest'}
          reason={accessInfo?.reason || defaultAccessReason}
          onLogin={onLogin}
          onUpgrade={handleUpgrade}
        />
      );
    }

    switch (activeTab) {
      case "Summary":
        return !canViewProtectedContent ? (
          <PremiumPrompt 
            isFeatured={isFeaturedEpisode}
            accessLevel={accessInfo?.accessLevel || 'guest'}
            reason={accessInfo?.reason || defaultAccessReason}
            onLogin={onLogin}
            onUpgrade={handleUpgrade}
          />
        ) : (
          <SummarySection
            summary={summary}
            isSummaryLoading={isSummaryLoading}
            onGenerateSummary={onGenerateSummary}
            player={ytPlayer}
            episodeId={episodeId}
            onTranslationStateChange={onSummaryTranslationStateChange}
          />
        );

      case "Mindmap":
        return !canViewProtectedContent ? (
          <PremiumPrompt 
            isFeatured={isFeaturedEpisode}
            accessLevel={accessInfo?.accessLevel || 'guest'}
            reason={accessInfo?.reason || defaultAccessReason}
            onLogin={onLogin}
            onUpgrade={handleUpgrade}
          />
        ) : (
          <MindMapSection
            mindmapData={mindmapData || content?.content?.content?.mindmap || ""}
            isLoading={isLoading}
            onGenerateMindmap={onGenerateMindmap}
            onTimestampClick={onTimestampClick}
            episodeId={episodeId}
          />
        );

      case "Transcript": {
        const hasTranscriptContent =
          Array.isArray(groupedSentences) && groupedSentences.length > 0;
        const transcriptSection = (
          <TranscriptSection
            groupedSentences={groupedSentences}
            youtubeTime={youtubeTime}
            ytPlayer={ytPlayer}
            userSettings={userSettings}
            wordHighlightEnabled={wordHighlightEnabled}
            practiceMode={practiceMode}
            practiceModeType={practiceModeType}
            vocabularyTerms={vocabularyTerms}
            setIsPlaying={setIsPlaying}
            showTranslation={showTranslation}
            isWordCheckerReady={isWordCheckerReady}
            isVideoMinimized={isVideoMinimized}
            episodeId={episodeId}
            episodeData={episodeData}
            episodeDuration={episodeDuration}
            isImmersiveMode={isImmersiveMode}
            activeSubtitleId={activeSubtitleId || undefined}
            onActiveSubtitleChange={onActiveSubtitleChange}
            onTranscriptUpdate={handleTranscriptUpdate}
            session={session}
            generationRecords={generationRecords}
            hasTwoBrokeGirlsAccess={hasTwoBrokeGirlsAccess}
            isTwoBrokeGirlsEpisode={isTwoBrokeGirlsEpisode}
            allowBasicPlanLite={allowBasicPlanLite}
            staticVocabularyTerms={staticVocabularyHighlights}
          />
        );

        if (canViewProtectedContent || unlockedByTranscriptGeneration) {
          if (hasTranscriptContent) {
            return transcriptSection;
          }

          return transcriptSection;
        }

        if (hasTranscriptContent) {
          return (
            <div className="mb-6">
              {transcriptSection}
            </div>
          );
        }

        return (
          <div>
            <div className="mb-6">
              <TranscriptSection
                groupedSentences={groupedSentences}
                youtubeTime={youtubeTime}
                ytPlayer={ytPlayer}
                userSettings={userSettings}
                wordHighlightEnabled={false}
                practiceMode={false}
                practiceModeType={practiceModeType}
                vocabularyTerms={[]}
                setIsPlaying={setIsPlaying}
                showTranslation={showTranslation}
                isWordCheckerReady={isWordCheckerReady}
                isVideoMinimized={isVideoMinimized}
                episodeId={episodeId}
                episodeData={episodeData}
                episodeDuration={episodeDuration}
                isImmersiveMode={false}
                activeSubtitleId={activeSubtitleId || undefined}
                onActiveSubtitleChange={onActiveSubtitleChange}
                onTranscriptUpdate={handleTranscriptUpdate}
                session={session}
                generationRecords={generationRecords}
                hasTwoBrokeGirlsAccess={hasTwoBrokeGirlsAccess}
                isTwoBrokeGirlsEpisode={isTwoBrokeGirlsEpisode}
                allowBasicPlanLite={allowBasicPlanLite}
                staticVocabularyTerms={staticVocabularyHighlights}
              />
            </div>
          </div>
        );
      }

      case "Phrase":
        if (!(canViewProtectedContent || unlockedByTranscriptGeneration)) {
          return (
            <PremiumPrompt 
              isFeatured={isFeaturedEpisode}
              accessLevel={accessInfo?.accessLevel || 'guest'}
              reason={accessInfo?.reason || defaultAccessReason}
              onLogin={onLogin}
              onUpgrade={handleUpgrade}
            />
          );
        }

        return (
          <PhraseSection
            groupedSentences={groupedSentences}
            vocabularyTerms={vocabularyTerms}
            vocabulary={vocabulary || object}
            hiddenTermKeys={hiddenVocabularyTermKeys}
            isWordCheckerReady={isWordCheckerReady}
            hasDictionarySelected={hasDictionarySelected}
            isVocabularyLoading={isVocabularyLoading}
            onTimestampClick={onTimestampClick}
            dictionaryLabel={dictionaryDisplayName}
          />
        );

      case "Vocabulary":
        if (!(canViewProtectedContent || unlockedByTranscriptGeneration)) {
          return (
            <PremiumPrompt 
              isFeatured={isFeaturedEpisode}
              accessLevel={accessInfo?.accessLevel || 'guest'}
              reason={accessInfo?.reason || defaultAccessReason}
              onLogin={onLogin}
              onUpgrade={handleUpgrade}
            />
          );
        }

        return (
          <VocabularySection
            vocabulary={vocabulary || object}
            isVocabularyLoading={isVocabularyLoading}
            onGenerateVocabulary={onGenerateVocabulary}
            onTimestampClick={onTimestampClick}
            episodeId={episodeId}
            onVocabularyUpdate={onVocabularyUpdate}
            onCardSwipe={onVocabularyCardSwipe}
            hiddenTermKeys={hiddenVocabularyTermKeys}
            onFilterHiddenTermsChange={onFilterHiddenTermsChange}
            session={session}
            userSettings={userSettings}
            episodeTitle={resolvedEpisodeTitle}
            episodeThumbnail={resolvedEpisodeThumbnail}
            transcript={content?.content?.content?.transcript}
            episodeLanguage={content?.content?.language || episodeData?.content?.language || 'Chinese'}
            episodeChannel={content?.content?.author || episodeData?.content?.author || null}
            episodeDescription={content?.content?.description || episodeData?.content?.description || null}
            episodeCategory={content?.content?.category || episodeData?.content?.category || null}
            episodeTags={
              Array.isArray(content?.content?.tags)
                ? content.content.tags
                : Array.isArray(episodeData?.content?.tags)
                  ? episodeData.content.tags
                  : null
            }
            episodePublishDate={content?.content?.publishDate || episodeData?.content?.publishDate || null}
            episodeDurationSeconds={
              typeof content?.content?.duration === 'number'
                ? content.content.duration
                : typeof episodeData?.content?.duration === 'number'
                  ? episodeData.content.duration
                  : episodeDuration ?? null
            }
            generationRecords={generationRecords}
            allowBasicPlanLite={allowBasicPlanLite}
          />
        );

      case "Quiz":
        if (!(canViewProtectedContent || unlockedByTranscriptGeneration)) {
          return (
            <PremiumPrompt 
              isFeatured={isFeaturedEpisode}
              accessLevel={accessInfo?.accessLevel || 'guest'}
              reason={accessInfo?.reason || defaultAccessReason}
              onLogin={onLogin}
              onUpgrade={handleUpgrade}
            />
          );
        }

        // 检查是否有 vocabulary 数据
        const hasVocabularyData = Boolean(
          vocabulary?.scenarios && 
          Array.isArray(vocabulary.scenarios) && 
          vocabulary.scenarios.length > 0
        );

        return (
          <QuizSection
            quizItems={quizItems}
            isLoading={isCleanReadLoading}
            onTimestampClick={onTimestampClick}
            episodeId={episodeId}
            episodeTitle={content?.content?.title || episodeData?.content?.title}
            episodeThumbnail={content?.content?.thumbnail || episodeData?.content?.thumbnail}
            onQuizItemsUpdate={handleQuizItemsUpdate}
            hasVocabulary={hasVocabularyData}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="sm:mt-2 mt-0 transition-all duration-300 ease-in-out">
      {renderContent()}
    </div>
  );
}; 
